/**
 * Hook for managing offline sync status and operations
 */

import { useState, useEffect, useCallback } from 'react'
import { offlineService, OfflineInvoice } from '../services/offlineService'

export interface UseOfflineSyncReturn {
  isOnline: boolean
  pendingCount: number
  syncingCount: number
  errorCount: number
  lastSync: number | null
  pendingInvoices: OfflineInvoice[]
  syncAll: () => Promise<void>
  syncInvoice: (localId: string) => Promise<boolean>
  getStats: () => ReturnType<typeof offlineService.getStats>
}

export function useOfflineSync(): UseOfflineSyncReturn {
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [pendingCount, setPendingCount] = useState(0)
  const [syncingCount, setSyncingCount] = useState(0)
  const [errorCount, setErrorCount] = useState(0)
  const [lastSync, setLastSync] = useState<number | null>(null)
  const [pendingInvoices, setPendingInvoices] = useState<OfflineInvoice[]>([])

  const updateStats = useCallback(() => {
    const stats = offlineService.getStats()
    setPendingCount(stats.pending)
    setSyncingCount(stats.syncing)
    setErrorCount(stats.error)
    setLastSync(stats.lastSync)
    setPendingInvoices(offlineService.getPendingInvoices())
  }, [])

  useEffect(() => {
    // Initial stats
    updateStats()

    // Listen to online/offline events
    const handleOnline = () => {
      setIsOnline(true)
      updateStats()
    }

    const handleOffline = () => {
      setIsOnline(false)
      updateStats()
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // Poll for stats updates every 5 seconds
    const interval = setInterval(updateStats, 5000)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      clearInterval(interval)
    }
  }, [updateStats])

  const syncAll = useCallback(async () => {
    await offlineService.syncAll()
    updateStats()
  }, [updateStats])

  const syncInvoice = useCallback(async (localId: string) => {
    const result = await offlineService.syncInvoice(localId)
    updateStats()
    return result
  }, [updateStats])

  const getStats = useCallback(() => {
    return offlineService.getStats()
  }, [])

  return {
    isOnline,
    pendingCount,
    syncingCount,
    errorCount,
    lastSync,
    pendingInvoices,
    syncAll,
    syncInvoice,
    getStats,
  }
}

